import { useTheme, useMediaQuery } from '@mui/material';

/**
 * useResponsive - Centralized responsive breakpoint hook
 *
 * Provides consistent breakpoint values across all components.
 * Use this hook instead of directly calling useMediaQuery with breakpoints.
 *
 * Breakpoint definitions (Material-UI defaults):
 *   xs: 0px    - Extra small (phones portrait)
 *   sm: 600px  - Small (phones landscape, small tablets)
 *   md: 900px  - Medium (tablets)
 *   lg: 1200px - Large (desktops)
 *   xl: 1536px - Extra large (large desktops)
 *
 * Usage:
 *   const { isMobile, isTablet, isDesktop, isSmallMobile } = useResponsive();
 *
 *   // In JSX:
 *   <Typography variant={isMobile ? "h6" : "h5"}>Title</Typography>
 *   <Button size={isMobile ? "small" : "medium"}>Click</Button>
 *   {isMobile && <BottomNavigation />}
 */
export default function useResponsive() {
  const theme = useTheme();

  // Primary breakpoints - use these for most responsive decisions
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));      // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-899px
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));       // >= 900px

  // Secondary breakpoints - for fine-grained control
  const isSmallMobile = useMediaQuery(theme.breakpoints.down(400));  // < 400px (very small phones)
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg'));  // >= 1200px

  // Combined breakpoints - for common use cases
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg')); // < 1200px (show mobile nav — desktop nav needs ~1100px+)
  const isTabletOrDesktop = useMediaQuery(theme.breakpoints.up('sm'));  // >= 600px

  return {
    // Primary - use these for most cases
    isMobile,           // Phones (< 600px) - use for: small buttons, simplified UI, bottom nav
    isTablet,           // Tablets (600-899px) - use for: medium layouts
    isDesktop,          // Desktops (>= 900px) - use for: full tables, sidebars

    // Secondary - for edge cases
    isSmallMobile,      // Very small phones (< 400px) - use for: extra compact layouts
    isLargeDesktop,     // Large screens (>= 1200px) - use for: wide layouts, extra columns

    // Combined - for navigation and layout decisions
    isMobileOrTablet,   // Touch devices (< 900px) - use for: bottom nav, touch targets
    isTabletOrDesktop,  // Non-phone devices (>= 600px) - use for: desktop nav

    // Utility values
    breakpoints: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  };
}

/**
 * Breakpoint constants for use in sx props
 *
 * Usage:
 *   import { BREAKPOINTS } from '../hooks/useResponsive';
 *
 *   <Box sx={{
 *     padding: { xs: 1, sm: 2, md: 3 },
 *     display: { xs: 'none', md: 'block' }
 *   }}>
 */
export const BREAKPOINTS = {
  // Recommended breakpoints for common patterns
  MOBILE: 'sm',      // Use with down() for mobile-only styles
  TABLET: 'md',      // Use with down() for tablet-and-below styles
  DESKTOP: 'lg',     // Use with up() for desktop-only styles

  // Standard MUI values
  XS: 'xs',
  SM: 'sm',
  MD: 'md',
  LG: 'lg',
  XL: 'xl',
};

/**
 * Responsive value helper for sx props
 *
 * Usage:
 *   import { responsive } from '../hooks/useResponsive';
 *
 *   <Box sx={{ padding: responsive(1, 2, 3) }}>
 *   // Results in: { xs: 1, sm: 2, md: 3 }
 */
export function responsive(mobile, tablet, desktop) {
  return {
    xs: mobile,
    sm: tablet ?? mobile,
    md: desktop ?? tablet ?? mobile,
  };
}
